import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from "react";
import { getCurrentSite, siteUrl } from "../../utils/SiteUtils";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import { getCountriesForTimezone } from "countries-and-timezones";
import { Site } from "../../config/sites";
import { getPathLanguage } from "../../utils/LangUtils";
import { useSearchParams } from "react-router-dom";

type CountryRedirectContext = {
  site?: Site;
  path?: string;
  url?: string;
  isAvailable?: boolean;
};

const countryRedirectContext = createContext<CountryRedirectContext>({});

const indexPath = "/";

export const CountryRedirectContextProvider = ({
  children
}: PropsWithChildren<void>) => {
  const [searchParams] = useSearchParams();
  const settings = useLocSettings();
  const { currentRegisteredPaths, pageAvailableOnSites } =
    useNavigationContext();
  const [values, setValues] = useState<CountryRedirectContext>({});

  useEffect(() => {
    //console.log("CountryRedirectContextProvider effect");
    if (Object.keys(currentRegisteredPaths).length === 0) return;
    function isAvailableOnSite(site: Site) {
      return (
        pageAvailableOnSites === undefined ||
        pageAvailableOnSites.includes(site)
      );
    }
    const currentSite = getCurrentSite();
    const localeTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZoneOverride = localStorage.getItem("timeZoneOverride");
    const countries = getCountriesForTimezone(
      timeZoneOverride || localeTimeZone
    );
    let country: string | undefined = countries[0]?.id?.toLowerCase();
    switch (country) {
      case "gb":
        country = "uk";
        break;
    }
    const site: Site = country as Site;
    const siteSetting = settings.sites[site];
    if (siteSetting && country !== currentSite) {
      //TODO get preferred language from somewhere else, Navigator?
      const lang = siteSetting.default_language;
      const isAvailable = isAvailableOnSite(site);
      let path = indexPath;
      if (isAvailable) {
        path =
          currentRegisteredPaths[lang] ||
          currentRegisteredPaths[siteSetting.default_language] ||
          currentRegisteredPaths["en"] ||
          currentRegisteredPaths["all"] ||
          indexPath;
      }
      path = `/${getPathLanguage(lang)}${path}`;
      const search =
        path !== indexPath && searchParams.toString()
          ? "?" + searchParams.toString()
          : "";
      const url = siteUrl(site, undefined, undefined, true) + path + search;
      console.log(`You should change to: ${url} (isAvailable: ${isAvailable})`);
      setValues({ site, url, path, isAvailable });
    } else {
      setValues({});
    }
  }, [settings, currentRegisteredPaths, pageAvailableOnSites, searchParams]);

  return (
    <countryRedirectContext.Provider value={values}>
      {children}
    </countryRedirectContext.Provider>
  );
};

export const useCountryRedirectContext = () =>
  useContext(countryRedirectContext);
